import { ApiEndpoint } from '../types/docs.types';

export const authenticationEndpoint: ApiEndpoint = {
  id: 'generate-access-token',
  title: 'Generate Access Token',
  method: 'GET',
  url: '{authUrl}/api/uaa/api/auth',
  description: 'Generate an access token using your API key and client ID. This token is required for all subsequent API calls.',
  parameters: [
    {
      name: 'x-api-key',
      type: 'string',
      required: true,
      location: 'header',
      description: 'Your API key provided by Aku',
      example: 'your-api-key-here'
    },
    {
      name: 'x-client-id',
      type: 'string',
      required: true,
      location: 'header',
      description: 'Your client ID provided by Aku',
      example: 'your-client-id-here'
    }
  ],
  responses: [
    {
      status: 200,
      description: 'Successfully generated access token',
      example: {
        accessToken: 'eyJhbGciOiJSUzI1NiJ9...',
        refreshToken: 'eyJhbGciOiJSUzI1NiJ9...',
        user: {
          id: '0d2d8b3a-f16e-46f9-905e-0f9f30f66c64',
          mobile: null,
          firstName: 'John',
          lastName: 'Doe',
          role: 'BUSINESS',
          roles: ['BUSINESS'],
          kycLevel: 'TIER_THREE',
          selfie: 'https://akumfb.blob.core.windows.net/selfies/null',
          accountPublicId: '507eddca-5edc-4b67-b3a9-cca071a02d5d'
        },
        properties: [
          {
            name: 'EMAIL_VALIDATED_DATE',
            value: '2024-08-06T11:03:56.839206850'
          }
        ]
      }
    },
    {
      status: 401,
      description: 'Invalid credentials',
      example: {
        error: 'Unauthorized',
        message: 'Invalid API key or client ID'
      }
    }
  ],
  codeExamples: [
    {
      language: 'curl',
      code: `curl --request GET \\
  --url https://staging.agent.aku.africa/api/uaa/api/auth \\
  --header 'x-api-key: your-api-key-here' \\
  --header 'x-client-id: your-client-id-here'`
    },
    {
      language: 'javascript',
      code: `const response = await fetch('https://staging.agent.aku.africa/api/uaa/api/auth', {
  method: 'GET',
  headers: {
    'x-api-key': 'your-api-key-here',
    'x-client-id': 'your-client-id-here'
  }
});

const data = await response.json();
console.log(data.accessToken);`
    },
    {
      language: 'python',
      code: `import requests

url = "https://staging.agent.aku.africa/api/uaa/api/auth"
headers = {
    "x-api-key": "your-api-key-here",
    "x-client-id": "your-client-id-here"
}

response = requests.get(url, headers=headers)
data = response.json()
print(data['accessToken'])`
    }
  ]
};

export const virtualAccountsEndpoints: ApiEndpoint[] = [
  {
    id: 'create-reserved-individual',
    title: 'Create Reserved Virtual Account - Individual',
    method: 'POST',
    url: '{baseUrl}/business/account/v2/reserve-account/requests',
    description: 'Create a reserved virtual account for an individual customer. This account is permanently linked and can be used for multiple transactions.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Individual account details',
      schema: {},
      example: {
        requestId: 'CRA1909031093131311',
        firstname: 'Vicky',
        middlename: 'Andor',
        lastname: 'Doe',
        mobile: '08078787754',
        dob: '2000-07-27',
        bvn: '22222222226'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Account created successfully',
        example: {
          requestId: 'CRA1909031093131311',
          accountName: 'Vicky Doe - Example Inc',
          type: 'RESERVE',
          accountNumber: '9066370955',
          status: 'ACTIVE'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url https://staging.agent.aku.africa/api/paas/business/account/v2/reserve-account/requests \\
  --header 'Authorization: Bearer your-access-token' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "requestId": "CRA1909031093131311",
    "firstname": "Vicky",
    "middlename": "Andor",
    "lastname": "Doe",
    "mobile": "08078787754",
    "dob": "2000-07-27",
    "bvn": "22222222226"
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/business/account/v2/reserve-account/requests', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your-access-token',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    requestId: 'CRA1909031093131311',
    firstname: 'Vicky',
    middlename: 'Andor',
    lastname: 'Doe',
    mobile: '08078787754',
    dob: '2000-07-27',
    bvn: '22222222226'
  })
});

const data = await response.json();
console.log(data);`
      }
    ]
  },
  {
    id: 'create-reserved-corporate',
    title: 'Create Reserved Virtual Account - Corporate',
    method: 'POST',
    url: '{baseUrl}/business/account/v2/reserve-account/requests/company',
    description: 'Create a reserved virtual account for a corporate customer. This account is permanently linked to the business entity.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'multipart/form-data',
      description: 'Corporate account details with document upload',
      schema: {},
      example: {
        file: 'Document file (PDF, PNG, JPG)',
        requestId: '444444445567743',
        companyName: 'Example Inc',
        mobile: '+2348179338262'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Corporate account created successfully',
        example: {
          requestId: 'e9baf260-557d-43cb-84e5-412bb63b13ce',
          accountName: 'Example Inc - polarbit 04',
          type: 'RESERVE',
          accountNumber: '9065343240',
          status: 'ACTIVE'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --location 'https://staging.agent.aku.africa/api/paas/business/account/v2/reserve-account/requests/company' \\
  --header 'accept: */*' \\
  --header 'Authorization: Bearer your-access-token' \\
  --form 'file=@"/path/to/your/document.pdf"' \\
  --form 'requestId="444444445567743"' \\
  --form 'companyName="Example Inc"' \\
  --form 'mobile="+2348179338262"'`
      }
    ]
  },
  {
    id: 'create-temporary-static',
    title: 'Create Temporary Static Virtual Account',
    method: 'POST',
    url: '{baseUrl}/business/account/v2/virtual-account/requests',
    description: 'Create a temporary virtual account with a fixed amount. Perfect for one-time payments.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Temporary account details with fixed amount',
      schema: {},
      example: {
        amount: '500',
        requestId: 'VEGS328H939003193298'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Temporary account created successfully',
        example: {
          accountName: 'Tony Soprano - Aku MFB',
          amount: 500,
          description: '',
          paymentRequestId: 'a335309f-a814-47de-b0d6-cd273ca56f1e',
          accountNumber: '2002405357',
          status: 'PENDING',
          accountNumberExpiry: '2025-08-06T16:04:00.052953974',
          requestId: 'VEGS328H939003193298',
          institutionName: 'Aku MFB',
          institutionCode: '090531'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url https://staging.agent.aku.africa/api/paas/business/account/v2/virtual-account/requests \\
  --header 'Authorization: Bearer your-access-token' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "amount": "500",
    "requestId": "VEGS328H939003193298"
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/business/account/v2/virtual-account/requests', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your-access-token',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    amount: '500',
    requestId: 'VEGS328H939003193298'
  })
});

const data = await response.json();
console.log(data);`
      }
    ]
  },
  {
    id: 'fund-transfer-single-credit',
    title: 'Fund Transfer Single Credit',
    method: 'POST',
    url: '{baseUrl}/banking/test?name=fundTransferSingleCredit',
    description: 'Transfer funds from a virtual or reserved account to a beneficiary account. Used by those who have created virtual or reserved accounts to send money.',
    parameters: [
      {
        name: 'x-api-key',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Your API key provided by Aku',
        example: '$apr1$0xpiuy83$80wyJVeTrN/UhcZuPA7pX.'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Fund transfer details including beneficiary and debitor information',
      schema: {},
      example: {
        beneficiaryAccountNumber: '09056153649',
        transactionAmount: '15000',
        beneficiaryName: 'Jane Smith',
        beneficiaryBvn: '5555555555',
        debitorName: 'John Doe',
        debitAccountNumber: '0123459789',
        debitorBvnNumber: '5555555555',
        transactionSessionId: '567439xx93903939977933',
        paymentReference: 't7r54rx454x55556444466o449',
        narration: 'testing',
        fee: '10'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Fund transfer initiated successfully',
        example: {
          success: true,
          transactionId: 't7r54rx454x55556444466o449',
          status: 'PENDING',
          message: 'Transfer initiated successfully'
        }
      },
      {
        status: 400,
        description: 'Invalid request parameters',
        example: {
          error: 'Bad Request',
          message: 'Invalid account number or insufficient funds'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --location 'https://staging.agent.aku.africa/api/paas/banking/test?name=fundTransferSingleCredit' \\
  --header 'x-api-key: your-api-key-here' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "beneficiaryAccountNumber": "09056153649",
    "transactionAmount": "15000",
    "beneficiaryName": "Jane Smith",
    "beneficiaryBvn": "5555555555",
    "debitorName": "John Doe",
    "debitAccountNumber": "0123459789",
    "debitorBvnNumber": "5555555555",
    "transactionSessionId": "567439xx93903939977933",
    "paymentReference": "t7r54rx454x55556444466o449",
    "narration": "testing",
    "fee": "10"
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/banking/test?name=fundTransferSingleCredit', {
  method: 'POST',
  headers: {
    'x-api-key': 'your-api-key-here',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    beneficiaryAccountNumber: '09056153649',
    transactionAmount: '15000',
    beneficiaryName: 'Jane Smith',
    beneficiaryBvn: '5555555555',
    debitorName: 'John Doe',
    debitAccountNumber: '0123459789',
    debitorBvnNumber: '5555555555',
    transactionSessionId: '567439xx93903939977933',
    paymentReference: 't7r54rx454x55556444466o449',
    narration: 'testing',
    fee: '10'
  })
});

const data = await response.json();
console.log(data);`
      },
      {
        language: 'python',
        code: `import requests

url = "https://staging.agent.aku.africa/api/paas/banking/test?name=fundTransferSingleCredit"
headers = {
    "x-api-key": "your-api-key-here",
    "Content-Type": "application/json"
}

payload = {
    "beneficiaryAccountNumber": "09056153649",
    "transactionAmount": "15000",
    "beneficiaryName": "Jane Smith",
    "beneficiaryBvn": "5555555555",
    "debitorName": "John Doe",
    "debitAccountNumber": "0123459789",
    "debitorBvnNumber": "5555555555",
    "transactionSessionId": "567439xx93903939977933",
    "paymentReference": "t7r54rx454x55556444466o449",
    "narration": "testing",
    "fee": "10"
}

response = requests.post(url, headers=headers, json=payload)
data = response.json()
print(data)`
      }
    ]
  },
  {
    id: 'reserve-account-transaction-status',
    title: 'Get Reserve Account Transaction Status',
    method: 'GET',
    url: '{baseUrl}/bank/transfer/v1/transfer-funds/status/reference/{originalReference}',
    description: 'Check the transaction status for reserve account transfer using its original reference',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'originalReference',
        type: 'string',
        required: true,
        location: 'path',
        description: 'The original reference of the transfer',
        example: 'dc6c010c-8069-4cf3-8af4-65e3b4b06914'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Reserve account transaction status retrieved successfully',
        example: {
          reference: 'dc6c010c-8069-4cf3-8af4-65e3b4b06914',
          status: 'COMPLETE',
          destination: '9005487656',
          destinationName: 'John Doe - Example Inc',
          requestId: 'dc6c010c-8069-4cf3-8af4-65e3b4b06914',
          responseCode: '00',
          responseMessage: 'Transaction approved',
          amount: 3410.89
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --location 'https://staging.agent.aku.africa/api/paas/bank/transfer/v1/transfer-funds/status/reference/dc6c010c-8069-4cf3-8af4-65e3b4b06914' \\
  --header 'Authorization: Bearer your-access-token'`
      }
    ]
  },
  {
    id: 'create-temporary-dynamic',
    title: 'Create Temporary Dynamic Virtual Account',
    method: 'POST',
    url: '{baseUrl}/business/account/v2/virtual-account/requests/dynamic',
    description: 'Create a temporary virtual account that accepts flexible amount and multiple payments.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Dynamic account configuration',
      schema: {},
      example: {
        amount: '100',
        requestId: 'DVA2384932240968778782'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Dynamic account created successfully',
        example: {
          accountName: 'John Doe - Aku MFB',
          amount: 100,
          description: '',
          paymentRequestId: '0b2fdfb2-3761-42db-a658-f4130e182420',
          accountNumber: '2075483256',
          status: 'PENDING',
          accountNumberExpiry: '2024-12-26T14:29:14.170820251',
          requestId: 'DVA2384932240968778782',
          institutionName: 'Aku MFB',
          institutionCode: '090531'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --location 'https://staging.agent.aku.africa/api/paas/business/account/v2/virtual-account/requests/dynamic' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer your-access-token' \\
  --data '{
    "amount": "100",
    "requestId": "DVA2384932240968778782"
  }'`
      }
    ]
  },
  {
    id: 'static-transaction-status',
    title: 'Get Temporary VA Transaction Status - Static',
    method: 'GET',
    url: '{baseUrl}/business/account/v2/virtual-account/payment-status?requestId={requestId}',
    description: 'Check the transaction status for payment into static virtual account using its request id',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'requestId',
        type: 'string',
        required: true,
        location: 'path',
        description: 'The request ID of the virtual account',
        example: 'VEGS328H939003193298'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Account status retrieved successfully',
        example: {
          requestId: 'DVA238493224099',
          status: 'COMPLETED',
          institutionCode: '000014',
          institution: 'ACCESS BANK',
          externalReference: '000014250820144752175569767287',
          originalReference: 'f50111eb-e77a-48b2-844c-28971ee9c5af',
          senderAccount: '0123456789',
          recipientAccount: '2010771123',
          amount: 500.000000000,
          type: 'VIRTUAL_ACCOUNT',
          amountRequested: 500.00
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url https://staging.agent.aku.africa/api/paas/business/account/v2/virtual-account/payment-status?requestId=DVA238493224099 \\
  --header 'Authorization: Bearer your-access-token'`
      }
    ]
  },
  {
    id: 'dynamic-transaction-status',
    title: 'Get Temporary VA Transaction Status - Dynamic',
    method: 'GET',
    url: '{baseUrl}/business/account/v2/virtual-account/payment-status/dynamic?requestId={requestId}',
    description: 'Check the transaction status for payments into dynamic virtual account using its request id',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'requestId',
        type: 'string',
        required: true,
        location: 'query',
        description: 'The request ID of the dynamic virtual account',
        example: 'DVA238493224099'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Dynamic account transaction status retrieved successfully',
        example: {
          requestId: '23884289428942424242',
          recipientAccount: '2064517522',
          status: 'PENDING',
          type: 'VIRTUAL_ACCOUNT',
          amountRequested: 1500,
          payments: [
            {
              payment: {
                ORIGINAL_REF: 'f4ef11a8-cd46-4de5-8386-2aea8850be4b',
                INSTITUTION_CODE: '000014',
                SENDER_ACCOUNT_NUMBER: '0123456789',
                RECIPIENT_ACCOUNT_NUMBER: '2064517522',
                AMOUNT: '3000.00000000000',
                EXTERNAL_REF: '000014250429085829174591350958',
                INSTITUTION_NAME: 'ACCESS BANK',
                STATUS: 'COMPLETED',
                AMOUNT_REQUESTED: '1500.00'
              }
            },
            {
              payment: {
                INSTITUTION_CODE: '090531',
                ORIGINAL_REF: 'edd54c96-3d75-4c0e-a406-26c9911dca11',
                SENDER_ACCOUNT_NUMBER: '5099196595',
                AMOUNT: '750.00',
                EXTERNAL_REF: 'edd54c96-3d75-4c0e-a406-26c9911dca11',
                INSTITUTION_NAME: 'Aku MFB',
                STATUS: 'COMPLETED',
                AMOUNT_REQUESTED: '1500.00'
              }
            }
          ]
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url https://staging.agent.aku.africa/api/paas/business/account/v2/virtual-account/payment-status/dynamic?requestId=23884289428942424242 \\
  --header 'Authorization: Bearer your-access-token'`
      }
    ]
  },
  {
    id: 'create-third-party-virtual-account',
    title: 'Create Third-Party Virtual Account',
    method: 'POST',
    url: '{baseUrl}/aku-mfb/virtual-account/requests',
    description: 'Create a third-party virtual account (non-AkuMFB). This allows you to generate temporary virtual accounts with customer details for collecting payments through partner banks.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Third-party virtual account details',
      schema: {},
      example: {
        requestId: 'TPV2024001234567890',
        firstname: 'John',
        lastname: 'Doe',
        mobile: '08179338262',
        amount: 1000,
        bvn: '22222222222',
        expiry: 10
      }
    },
    responses: [
      {
        status: 200,
        description: 'Third-party virtual account created successfully',
        example: {
          accountName: 'John Doe',
          amount: 1000,
          description: '',
          paymentRequestId: '8837227626',
          accountNumber: '5880064806',
          status: 'PENDING',
          accountNumberExpiry: null,
          requestId: '8837227626',
          institutionName: 'Stanbic',
          institutionCode: '000012'
        }
      },
      {
        status: 400,
        description: 'Invalid request parameters',
        example: {
          error: 'Bad Request',
          message: 'Invalid BVN or missing required fields'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --location 'https://staging.agent.aku.africa/api/paas/aku-mfb/virtual-account/requests' \\
  --header 'accept: */*' \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer your-access-token' \\
  --data '{
    "requestId": "TPV2024001234567890",
    "firstname": "John",
    "lastname": "Doe",
    "mobile": "08179338262",
    "amount": 1000,
    "bvn": "22222222222",
    "expiry": 10
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/aku-mfb/virtual-account/requests', {
  method: 'POST',
  headers: {
    'accept': '*/*',
    'Authorization': 'Bearer your-access-token',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    requestId: 'TPV2024001234567890',
    firstname: 'John',
    lastname: 'Doe',
    mobile: '08179338262',
    amount: 1000,
    bvn: '22222222222',
    expiry: 10
  })
});

const data = await response.json();
console.log(data);`
      },
      {
        language: 'python',
        code: `import requests

url = "https://staging.agent.aku.africa/api/paas/aku-mfb/virtual-account/requests"
headers = {
    "accept": "*/*",
    "Authorization": "Bearer your-access-token",
    "Content-Type": "application/json"
}

payload = {
    "requestId": "TPV2024001234567890",
    "firstname": "John",
    "lastname": "Doe",
    "mobile": "08179338262",
    "amount": 1000,
    "bvn": "22222222222",
    "expiry": 10
}

response = requests.post(url, headers=headers, json=payload)
data = response.json()
print(data)`
      }
    ]
  }
];

export const payoutsEndpoints: ApiEndpoint[] = [
  {
    id: 'get-banks',
    title: 'Get Bank List',
    method: 'GET',
    url: '{baseUrl}/bank/transfer/v1/banks',
    description: 'Retrieve a list of all supported banks for transfers. This includes bank codes, names, and logos.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'List of supported banks',
        example: {
          content: [
            {
              id: '7e21fd55-f3f0-4c63-a4c9-61137cdd778f',
              code: '000016',
              name: 'FIRST BANK OF NIGERIA',
              logoUrl: 'https://akumfb.blob.core.windows.net/assets/ASSET/7e21fd55-f3f0-4c63-a4c9-61137cdd778f.svg'
            },
            {
              id: '0a76ed31-2a96-4cf2-9186-231f404ffdcb',
              code: '000013',
              name: 'GTBANK PLC',
              logoUrl: 'https://akumfb.blob.core.windows.net/assets/ASSET/0a76ed31-2a96-4cf2-9186-231f404ffdcb.svg'
            }
          ],
          totalElements: 570,
          totalPages: 1,
          size: 570,
          number: 0,
          first: true,
          last: true,
          empty: false
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url https://staging.agent.aku.africa/api/paas/bank/transfer/v1/banks \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/bank/transfer/v1/banks', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const data = await response.json();
console.log(data.content); // Array of banks`
      }
    ]
  },
  {
    id: 'name-enquiry',
    title: 'Name Enquiry',
    method: 'GET',
    url: '{baseUrl}/bank/transfer/v1/banks/code/{bankCode}/accounts/account-number/{accountNumber}/name',
    description: 'Verify account details before initiating a transfer. Returns the account holder name for the given account number and bank.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'bankCode',
        type: 'string',
        required: true,
        location: 'path',
        description: 'Bank code from the banks list',
        example: '000023'
      },
      {
        name: 'accountNumber',
        type: 'string',
        required: true,
        location: 'path',
        description: '10-digit account number',
        example: '0164524367'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Account name resolved successfully',
        example: {
          bank: '000023',
          accountNumber: '0164524367',
          name: 'JANE NKANOR'
        }
      },
      {
        status: 404,
        description: 'Account not found',
        example: {
          error: 'Account not found',
          message: 'The specified account number does not exist'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url https://staging.agent.aku.africa/api/paas/bank/transfer/v1/banks/code/000023/accounts/account-number/0164524367/name \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const bankCode = '000023';
const accountNumber = '0164524367';

const response = await fetch(\`https://staging.agent.aku.africa/api/paas/bank/transfer/v1/banks/code/\${bankCode}/accounts/account-number/\${accountNumber}/name\`, {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const data = await response.json();
console.log(data.name); // Account holder name`
      }
    ]
  },
  {
    id: 'initiate-transfer',
    title: 'Initiate Transfer',
    method: 'POST',
    url: '{baseUrl}/bank/transfer/v1/transfer-funds',
    description: 'Send money to a bank account. The transfer can be processed immediately or queued based on your account settings.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Transfer details',
      schema: {},
      example: {
        amount: 100,
        bankCode: '000023',
        recipientAccountNumber: '0164387486',
        narration: 'This is a test payment',
        requestId: '2921912900912092901329230'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Transfer initiated successfully',
        example: {
          reference: '249d86ac-db0a-4e9b-81e6-ee138520bbe0',
          status: 'PENDING',
          destination: '',
          destinationName: '',
          requestId: '292191290091209222329230',
          responseCode: '9301',
          responseMessage: 'Transaction pending: requery',
          amount: 100
        }
      },
      {
        status: 400,
        description: 'Invalid request parameters',
        example: {
          error: 'Bad Request',
          message: 'Invalid account number format'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url https://staging.agent.aku.africa/api/paas/bank/transfer/v1/transfer-funds \\
  --header 'Authorization: Bearer your-access-token' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "amount": 100,
    "bankCode": "000023",
    "recipientAccountNumber": "0164387486",
    "narration": "This is a test payment",
    "requestId": "2921912900912092901329230"
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/bank/transfer/v1/transfer-funds', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your-access-token',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    amount: 100,
    bankCode: '000023',
    recipientAccountNumber: '0164387486',
    narration: 'This is a test payment',
    requestId: '2921912900912092901329230'
  })
});

const data = await response.json();
console.log(data.reference); // Transfer reference`
      }
    ]
  },
  {
    id: 'get-transfer-status',
    title: 'Get Transfer Status',
    method: 'GET',
    url: '{baseUrl}/bank/transfer/v1/transfer-status/reference/{requestId}',
    description: 'Check the current status of a transfer using its request ID. Track whether the transfer is PENDING, COMPLETE, or FAILED.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'reference',
        type: 'string',
        required: true,
        location: 'path',
        description: 'Transfer reference returned from initiate transfer',
        example: '2ad3a94f-d51a-4bd1-ab2a-e0b80c62e095'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Transfer status retrieved successfully',
        example: {
          reference: '249d86ac-db0a-4e9b-81e6-ee138520bbe0',
          status: 'COMPLETE',
          destination: '0164386507',
          destinationName: '',
          requestId: '292191290091209222329230',
          responseCode: '00',
          responseMessage: 'Transaction approved',
          amount: 100.00
        }
      },
      {
        status: 404,
        description: 'Transfer not found',
        example: {
          error: 'Transfer not found',
          message: 'No transfer found with the provided reference'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url https://staging.agent.aku.africa/api/paas/bank/transfer/v1/transfer-status/reference/292191290091209222329230 \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const reference = '2ad3a94f-d51a-4bd1-ab2a-e0b80c62e095';

const response = await fetch(\`https://staging.agent.aku.africa/api/paas/bank/transfer/v1/transfer-status/reference\${requestId}\`, {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const data = await response.json();
console.log(data.status); // Transfer status`
      }
    ]
  }
];

export const accountEndpoints: ApiEndpoint[] = [
  {
    id: 'reserve-account-details',
    title: 'Get Reserve Account Details',
    method: 'GET',
    url: '{baseUrl}/business/account/v2/reserve-account?accountNumber={accountNumber}',
    description: 'Retrieve reserve account details using the account number.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'accountNumber',
        type: 'string',
        required: true,
        location: 'query',
        description: 'The reserve account number to get details for',
        example: '9057169759'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Reserve account details',
        example: {
          firstname: 'Nando Gota - Example Inc',
          accountNumber: '9057169759',
          status: 'ACTIVE',
          balance: 4000.21
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --location 'https://staging.agent.aku.africa/api/paas/business/account/v2/reserve-account?accountNumber=9057169759' \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const accountNumber = '9057169759';

const response = await fetch(\`https://staging.agent.aku.africa/api/paas/business/account/v2/reserve-account?accountNumber=\${accountNumber}\`, {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const data = await response.json();
console.log(\`Account details:\`, data);`
      }
    ]
  },
  {
    id: 'single-account',
    title: 'Get Account Balance',
    method: 'GET',
    url: '{baseUrl}/business/account/v2/single?accountNumber={accountNumber}',
    description: 'Retrieve account balance using the account number.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token obtained from authentication',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'accountNumber',
        type: 'string',
        required: true,
        location: 'query',
        description: 'The account number to get balance for',
        example: '9057169759'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Single account balance information',
        example: {
          accountNumber: '9057169759',
          accountBalance: 4000.21
        }
      },
      {
        status: 404,
        description: 'Account not found',
        example: {
          error: 'Account not found',
          message: 'The specified account number does not exist or is not accessible'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --location 'https://staging.agent.aku.africa/api/paas/business/account/v2/single?accountNumber=9057169759' \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const accountNumber = '9057169759';

const response = await fetch(\`https://staging.agent.aku.africa/api/paas/business/account/v2/single?accountNumber=\${accountNumber}\`, {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const data = await response.json();
console.log(\`Account Balance: ₦\${data.accountBalance.toLocaleString()}\`);`
      }
    ]
  }
];

export const billPaymentEndpoints: ApiEndpoint[] = [
  {
    id: 'get-bill-payment-categories',
    title: 'Get Product Categories',
    method: 'GET',
    url: '{baseUrl}/bill-payment/v1/options/services/category',
    description: 'Lists all bill payment categories available, each with its display label, icon and ordering.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token with the BILL_PAYMENT_API authority',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Array of product categories',
        example: [
          {
            productType: 'AIRTIME',
            label: 'Airtime',
            iconUrl: 'https://example.com/icons/airtime.png',
            display: true,
            order: 1
          },
          {
            productType: 'DATA',
            label: 'Data',
            iconUrl: 'https://example.com/icons/data.png',
            display: true,
            order: 2
          },
          {
            productType: 'ELECTRICITY',
            label: 'Electricity',
            iconUrl: 'https://example.com/icons/electricity.png',
            display: true,
            order: 3
          }
        ]
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services/category \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services/category', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const categories = await response.json();
console.log(categories);`
      }
    ]
  },
  {
    id: 'get-service-providers',
    title: 'Get Service Providers By Type',
    method: 'GET',
    url: '{baseUrl}/bill-payment/v1/options/services',
    description: 'Lists the billers offering products of the given type.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token with the BILL_PAYMENT_API authority',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'type',
        type: 'enum',
        required: true,
        location: 'query',
        description: 'Category type: TV | ELECTRICITY | INTERNET | EDUCATION | INSURANCE | BET | AIRTIME | DATA | EVENT',
        example: 'AIRTIME'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Array of service providers',
        example: [
          {
            serviceId: 'mtn',
            providerName: 'MTN Nigeria',
            logoUrl: 'https://example.com/logos/mtn.png',
            smallLogoUrl: 'https://example.com/logos/mtn-sm.png',
            minimumAmount: 50,
            maximumAmount: 50000
          },
          {
            serviceId: 'airtel',
            providerName: 'Airtel Nigeria',
            logoUrl: 'https://example.com/logos/airtel.png',
            smallLogoUrl: 'https://example.com/logos/airtel-sm.png',
            minimumAmount: 50,
            maximumAmount: 50000
          }
        ]
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url 'https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services?type=AIRTIME' \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services?type=AIRTIME', {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const providers = await response.json();
console.log(providers);`
      }
    ]
  },
  {
    id: 'get-all-products',
    title: 'Get All Products For Service',
    method: 'GET',
    url: '{baseUrl}/bill-payment/v1/options/services/{serviceId}/products/all',
    description: 'Unpaged list of every product a biller offers for the given type. Prefer the paged variant for billers with large catalogues.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token with the BILL_PAYMENT_API authority',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'serviceId',
        type: 'string',
        required: true,
        location: 'path',
        description: 'Biller identifier from the service providers endpoint',
        example: 'mtn'
      },
      {
        name: 'type',
        type: 'enum',
        required: true,
        location: 'query',
        description: 'Must match the type used to find the service',
        example: 'AIRTIME'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Array of products',
        example: [
          {
            id: '550e8400-e29b-41d4-a716-446655440000',
            serviceId: 'mtn',
            price: 100,
            productName: 'MTN ₦100 Airtime',
            detailDescription: 'Top up ₦100 airtime on MTN network',
            status: 'ACTIVE',
            variationId: 'prepaid',
            fixedPrice: true,
            fields: [],
            minimumPayableAmount: null,
            displayMinimumAmount: false
          }
        ]
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url 'https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services/mtn/products/all?type=AIRTIME' \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const serviceId = 'mtn';
const response = await fetch(\`https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services/\${serviceId}/products/all?type=AIRTIME\`, {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const products = await response.json();
console.log(products);`
      }
    ]
  },
  {
    id: 'get-products-paged',
    title: 'Get Products For Service (Paged)',
    method: 'GET',
    url: '{baseUrl}/bill-payment/v1/options/services/{serviceId}/products',
    description: 'Paged list of products a biller offers for the given type.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token with the BILL_PAYMENT_API authority',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'serviceId',
        type: 'string',
        required: true,
        location: 'path',
        description: 'Biller identifier from the service providers endpoint',
        example: 'mtn'
      },
      {
        name: 'type',
        type: 'enum',
        required: true,
        location: 'query',
        description: 'Product category type',
        example: 'AIRTIME'
      },
      {
        name: 'page',
        type: 'integer',
        required: false,
        location: 'query',
        description: 'Zero-based page number. Defaults to 0.',
        example: '0'
      },
      {
        name: 'size',
        type: 'integer',
        required: false,
        location: 'query',
        description: 'Page size. Defaults to 20.',
        example: '20'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Paginated list of products',
        example: {
          content: [
            {
              id: '550e8400-e29b-41d4-a716-446655440000',
              serviceId: 'mtn',
              price: 100,
              productName: 'MTN ₦100 Airtime',
              status: 'ACTIVE',
              fixedPrice: true
            }
          ],
          number: 0,
          size: 20,
          totalElements: 5,
          totalPages: 1,
          last: true
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url 'https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services/mtn/products?type=AIRTIME&page=0&size=20' \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const serviceId = 'mtn';
const response = await fetch(\`https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/services/\${serviceId}/products?type=AIRTIME&page=0&size=20\`, {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const page = await response.json();
console.log(\`Page \${page.number + 1} of \${page.totalPages}, \${page.totalElements} total products\`);`
      }
    ]
  },
  {
    id: 'get-product-by-id',
    title: 'Get Product By Id',
    method: 'GET',
    url: '{baseUrl}/bill-payment/v1/options/{productId}',
    description: 'Fetches a single product by its public id. Use this to re-check price and status immediately before initiating a payment.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token with the BILL_PAYMENT_API authority',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      },
      {
        name: 'productId',
        type: 'UUID',
        required: true,
        location: 'path',
        description: 'Product public id from the products endpoint',
        example: '550e8400-e29b-41d4-a716-446655440000'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Product details',
        example: {
          id: '550e8400-e29b-41d4-a716-446655440000',
          serviceId: 'mtn',
          price: 100,
          productName: 'MTN ₦100 Airtime',
          detailDescription: 'Top up ₦100 airtime on MTN network',
          status: 'ACTIVE',
          variationId: 'prepaid',
          fixedPrice: true,
          fields: [],
          minimumPayableAmount: null,
          displayMinimumAmount: false
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url 'https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/550e8400-e29b-41d4-a716-446655440000' \\
  --header 'Authorization: Bearer your-access-token'`
      },
      {
        language: 'javascript',
        code: `const productId = '550e8400-e29b-41d4-a716-446655440000';
const response = await fetch(\`https://staging.agent.aku.africa/api/paas/bill-payment/v1/options/\${productId}\`, {
  method: 'GET',
  headers: {
    'Authorization': 'Bearer your-access-token'
  }
});

const product = await response.json();
console.log(product);`
      }
    ]
  },
  {
    id: 'get-customer-information',
    title: 'Get Customer Information',
    method: 'POST',
    url: '{baseUrl}/bill-payment/v1/customer-information',
    description: 'Validates a customer with the biller and returns what the biller knows about them. Supported by electricity, TV and similar billers; not all products support lookup.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token with the BILL_PAYMENT_API authority',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Customer lookup details',
      schema: {},
      example: {
        customerId: '12345678901',
        serviceId: 'ikeja-electric',
        variationId: 'prepaid'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Customer details from the biller',
        example: {
          customerId: '12345678901',
          customerName: 'John Doe',
          customerAddress: '12 Marina Road, Lagos',
          customerArrears: '0',
          customerCategory: 'NON_MD',
          minimumAmount: 1000,
          displayMinimum: true
        }
      },
      {
        status: 400,
        description: 'Biller lookup failed',
        example: {
          error: 'Bad Request',
          message: 'We are unable to retrieve customer information at this time.'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url https://staging.agent.aku.africa/api/paas/bill-payment/v1/customer-information \\
  --header 'Authorization: Bearer your-access-token' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "customerId": "12345678901",
    "serviceId": "ikeja-electric",
    "variationId": "prepaid"
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/bill-payment/v1/customer-information', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your-access-token',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    customerId: '12345678901',
    serviceId: 'ikeja-electric',
    variationId: 'prepaid'
  })
});

const customer = await response.json();
console.log(customer.customerName);`
      }
    ]
  },
  {
    id: 'initiate-bill-payment',
    title: 'Initiate Bill Payment',
    method: 'POST',
    url: '{baseUrl}/bill-payment/v1/initiate',
    description: 'Stages a bill payment. Validates the product, price bounds and your account, reserves the transaction and returns a reference. Nothing is debited until you authorize. The account debited is derived from your bearer token — a partner can only spend from its own agent account.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token with the BILL_PAYMENT_API authority. The debited account is derived from this token.',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Bill payment details. There is no fundingSourceId — the debited account is derived from your bearer token.',
      schema: {},
      example: {
        billPaymentType: 'AIRTIME',
        customerId: '08012345678',
        productId: '550e8400-e29b-41d4-a716-446655440000',
        recipientPhone: '08012345678',
        amount: 500
      }
    },
    responses: [
      {
        status: 200,
        description: 'Payment staged. Call /authorize with the returned originalRef to complete.',
        example: {
          originalRef: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890',
          status: 'PENDING',
          transactionStatus: 'PENDING'
        }
      },
      {
        status: 400,
        description: 'Validation failure',
        example: {
          error: 'Bad Request',
          message: 'Amount less than minimum allowed of ₦100'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url https://staging.agent.aku.africa/api/paas/bill-payment/v1/initiate \\
  --header 'Authorization: Bearer your-access-token' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "billPaymentType": "AIRTIME",
    "customerId": "08012345678",
    "productId": "550e8400-e29b-41d4-a716-446655440000",
    "recipientPhone": "08012345678",
    "amount": 500
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/bill-payment/v1/initiate', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your-access-token',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    billPaymentType: 'AIRTIME',
    customerId: '08012345678',
    productId: '550e8400-e29b-41d4-a716-446655440000',
    recipientPhone: '08012345678',
    amount: 500
  })
});

const { originalRef, transactionStatus } = await response.json();
console.log('Reference:', originalRef, 'Status:', transactionStatus);`
      }
    ]
  },
  {
    id: 'authorize-bill-payment',
    title: 'Authorize Bill Payment',
    method: 'POST',
    url: '{baseUrl}/bill-payment/v1/authorize',
    description: 'Completes a payment staged by initiate. Debits your agent account, pays the biller and returns the outcome. No OTP or PIN required — your bearer token is checked against the agent account that staged the payment. A processing response is not a failure — poll rather than retrying with a new reference to avoid double-billing.',
    parameters: [
      {
        name: 'Authorization',
        type: 'string',
        required: true,
        location: 'header',
        description: 'Bearer token — must belong to the same partner that initiated the payment',
        example: 'Bearer eyJhbGciOiJSUzI1NiJ9...'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Reference returned by initiate',
      schema: {},
      example: {
        reference: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Payment completed successfully',
        example: {
          transactionStatus: 'COMPLETE',
          externalReference: 'BILLER-REF-12345',
          shortRef: 'AKU-001',
          additionalData: null
        }
      },
      {
        status: 400,
        description: 'Biller rejection or processing',
        example: {
          error: 'Bad Request',
          message: 'An error occurred while completing the transaction. We will reverse your funds.'
        }
      },
      {
        status: 403,
        description: 'Reference belongs to another partner',
        example: {
          error: 'Forbidden',
          message: 'Please provide correct user information'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url https://staging.agent.aku.africa/api/paas/bill-payment/v1/authorize \\
  --header 'Authorization: Bearer your-access-token' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "reference": "a1b2c3d4-e5f6-7890-abcd-ef1234567890"
  }'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/api/paas/bill-payment/v1/authorize', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer your-access-token',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    reference: 'a1b2c3d4-e5f6-7890-abcd-ef1234567890'
  })
});

const result = await response.json();
if (result.transactionStatus === 'COMPLETE') {
  console.log('Payment successful:', result.externalReference);
}`
      }
    ]
  }
];

export const customerCryptoEndpoints: ApiEndpoint[] = [
  {
    id: 'register-customer',
    title: 'Register Customer',
    method: 'POST',
    url: '{baseUrl}/business/crypto/customers/register',
    description: 'Registers a new customer with their email address. A verification email will be sent.',
    parameters: [
      {
        name: 'email',
        type: 'string',
        required: true,
        location: 'query',
        description: 'Valid email address for the customer',
        example: 'user@example.com'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Registration successful',
        example: {
          customerId: '550e8400-e29b-41d4-a716-446655440000',
          email: 'user@example.com',
          message: 'Registration successful. Please check your email to verify your account.'
        }
      },
      {
        status: 400,
        description: 'Email already registered',
        example: {
          error: 'Bad Request',
          message: 'Email already registered'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url 'https://staging.agent.aku.africa/business/crypto/customers/register?email=user@example.com'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/business/crypto/customers/register?email=user@example.com', {
  method: 'POST'
});

const data = await response.json();
console.log('Customer ID:', data.customerId);`
      }
    ]
  },
  {
    id: 'verify-email',
    title: 'Verify Email',
    method: 'GET',
    url: '{baseUrl}/business/crypto/customers/verify-email',
    description: 'Verifies a customer email using the token sent via email after registration.',
    parameters: [
      {
        name: 'token',
        type: 'string',
        required: true,
        location: 'query',
        description: 'Verification token from the email link',
        example: 'abc123-def456-ghi789'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Email verified',
        example: 'Email verified successfully'
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url 'https://staging.agent.aku.africa/business/crypto/customers/verify-email?token=abc123-def456-ghi789'`
      },
      {
        language: 'javascript',
        code: `const token = 'abc123-def456-ghi789';
const response = await fetch(\`https://staging.agent.aku.africa/business/crypto/customers/verify-email?token=\${token}\`);

const message = await response.json();
console.log(message);`
      }
    ]
  },
  {
    id: 'resend-verification',
    title: 'Resend Verification Email',
    method: 'POST',
    url: '{baseUrl}/business/crypto/customers/resend-verification',
    description: 'Resends the verification email to a registered customer.',
    parameters: [
      {
        name: 'email',
        type: 'string',
        required: true,
        location: 'query',
        description: 'Registered email address',
        example: 'user@example.com'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Verification email sent',
        example: 'Verification email sent successfully'
      },
      {
        status: 404,
        description: 'Customer not found',
        example: {
          error: 'Not Found',
          message: 'Customer not found'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url 'https://staging.agent.aku.africa/business/crypto/customers/resend-verification?email=user@example.com'`
      },
      {
        language: 'javascript',
        code: `const response = await fetch('https://staging.agent.aku.africa/business/crypto/customers/resend-verification?email=user@example.com', {
  method: 'POST'
});

const message = await response.json();
console.log(message);`
      }
    ]
  },
  {
    id: 'add-identification',
    title: 'Add Identification (KYC)',
    method: 'POST',
    url: '{baseUrl}/business/crypto/customer/{customerId}/identification',
    description: 'Submits a customer identification document for KYC verification. Supports NIN and BVN.',
    parameters: [
      {
        name: 'customerId',
        type: 'UUID',
        required: true,
        location: 'path',
        description: "Customer's unique identifier from registration",
        example: '550e8400-e29b-41d4-a716-446655440000'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'ID document details',
      schema: {},
      example: {
        idType: 'NIN',
        idNumber: '12345678901'
      }
    },
    responses: [
      {
        status: 200,
        description: 'Identification submitted',
        example: 'Identification has been sent successfully'
      },
      {
        status: 404,
        description: 'Customer not found',
        example: {
          error: 'Not Found',
          message: 'Customer not found'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url 'https://staging.agent.aku.africa/business/crypto/customer/550e8400-e29b-41d4-a716-446655440000/identification' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "idType": "NIN",
    "idNumber": "12345678901"
  }'`
      },
      {
        language: 'javascript',
        code: `const customerId = '550e8400-e29b-41d4-a716-446655440000';
const response = await fetch(\`https://staging.agent.aku.africa/business/crypto/customer/\${customerId}/identification\`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    idType: 'NIN',
    idNumber: '12345678901'
  })
});

const message = await response.json();
console.log(message);`
      }
    ]
  },
  {
    id: 'selfie-compare',
    title: 'Selfie Compare (Liveness Check)',
    method: 'POST',
    url: '{baseUrl}/business/crypto/selfie-compare/{customerId}',
    description: 'Performs selfie comparison and liveness check for identity verification. Submit a selfie image alongside liveness images as base64-encoded strings.',
    parameters: [
      {
        name: 'customerId',
        type: 'UUID',
        required: true,
        location: 'path',
        description: "Customer's unique identifier",
        example: '550e8400-e29b-41d4-a716-446655440000'
      }
    ],
    requestBody: {
      type: 'application/json',
      description: 'Base64-encoded selfie and liveness images',
      schema: {},
      example: {
        selfieImage: 'base64_encoded_selfie_image',
        livenessImages: [
          'base64_encoded_liveness_image_1',
          'base64_encoded_liveness_image_2'
        ]
      }
    },
    responses: [
      {
        status: 200,
        description: 'Verification result',
        example: {
          message: 'Verification successful',
          status: 'APPROVED',
          userId: '550e8400-e29b-41d4-a716-446655440000',
          updatedAt: '2024-01-15T10:30:00Z',
          createdAt: '2024-01-15T10:30:00Z'
        }
      },
      {
        status: 422,
        description: 'KYC not complete',
        example: {
          error: 'Unprocessable Entity',
          message: 'Customer is not kyc verified'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url 'https://staging.agent.aku.africa/business/crypto/selfie-compare/550e8400-e29b-41d4-a716-446655440000' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "selfieImage": "base64_encoded_selfie_image",
    "livenessImages": [
      "base64_encoded_liveness_image_1",
      "base64_encoded_liveness_image_2"
    ]
  }'`
      },
      {
        language: 'javascript',
        code: `const customerId = '550e8400-e29b-41d4-a716-446655440000';
const response = await fetch(\`https://staging.agent.aku.africa/business/crypto/selfie-compare/\${customerId}\`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({
    selfieImage: selfieBase64,
    livenessImages: [liveness1Base64, liveness2Base64]
  })
});

const result = await response.json();
console.log('Verification status:', result.status);`
      }
    ]
  },
  {
    id: 'get-quote',
    title: 'Get Quote',
    method: 'GET',
    url: '{baseUrl}/business/crypto/customers/quote',
    description: 'Gets a quote for a crypto transaction including the exchange rate, fees, and local currency amount.',
    parameters: [
      {
        name: 'customerId',
        type: 'UUID',
        required: true,
        location: 'query',
        description: "Customer's unique identifier",
        example: '550e8400-e29b-41d4-a716-446655440000'
      },
      {
        name: 'requestId',
        type: 'string',
        required: true,
        location: 'query',
        description: 'Unique request identifier for idempotency',
        example: 'REQ123'
      },
      {
        name: 'destinationCurrency',
        type: 'string',
        required: true,
        location: 'query',
        description: 'Target currency code',
        example: 'USD'
      },
      {
        name: 'amount',
        type: 'string',
        required: true,
        location: 'query',
        description: 'Amount to convert',
        example: '1000'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Quote details',
        example: {
          amount: 100.00,
          currency: 'USD',
          localAmount: 150000.00,
          localCurrency: 'NGN',
          exchangeRate: 1500.00,
          fee: 2.50,
          instructions: 'Transfer NGN 150,000 to complete the transaction'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url 'https://staging.agent.aku.africa/business/crypto/customers/quote?customerId=550e8400-e29b-41d4-a716-446655440000&requestId=REQ123&destinationCurrency=USD&amount=1000'`
      },
      {
        language: 'javascript',
        code: `const params = new URLSearchParams({
  customerId: '550e8400-e29b-41d4-a716-446655440000',
  requestId: 'REQ123',
  destinationCurrency: 'USD',
  amount: '1000'
});

const response = await fetch(\`https://staging.agent.aku.africa/business/crypto/customers/quote?\${params}\`);
const quote = await response.json();
console.log(\`Rate: \${quote.exchangeRate} NGN/USD, Fee: \${quote.fee}\`);`
      }
    ]
  },
  {
    id: 'accept-quote',
    title: 'Accept Quote',
    method: 'POST',
    url: '{baseUrl}/business/crypto/customers/quote/{requestId}/accept',
    description: 'Accepts a previously generated quote and creates a virtual account for payment. The customer should transfer the local amount to the returned account number before it expires.',
    parameters: [
      {
        name: 'requestId',
        type: 'string',
        required: true,
        location: 'path',
        description: 'The quote request ID',
        example: 'REQ123'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Virtual account created for payment',
        example: {
          accountName: 'Aku MFB/John Doe',
          amount: 150000.00,
          description: 'Payment for crypto purchase',
          paymentRequestId: 'PAY123456',
          accountNumber: '1234567890',
          status: 'PENDING',
          accountNumberExpiry: '2024-01-15T11:30:00',
          requestId: 'REQ123',
          institutionName: 'Aku MFB',
          institutionCode: '090123'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url 'https://staging.agent.aku.africa/business/crypto/customers/quote/REQ123/accept'`
      },
      {
        language: 'javascript',
        code: `const requestId = 'REQ123';
const response = await fetch(\`https://staging.agent.aku.africa/business/crypto/customers/quote/\${requestId}/accept\`, {
  method: 'POST'
});

const account = await response.json();
console.log(\`Pay ₦\${account.amount.toLocaleString()} to account \${account.accountNumber} before \${account.accountNumberExpiry}\`);`
      }
    ]
  },
  {
    id: 'reject-quote',
    title: 'Reject Quote',
    method: 'POST',
    url: '{baseUrl}/business/crypto/customers/quote/{requestId}/reject',
    description: 'Rejects and cancels a previously generated quote.',
    parameters: [
      {
        name: 'requestId',
        type: 'string',
        required: true,
        location: 'path',
        description: 'The quote request ID to cancel',
        example: 'REQ123'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Quote rejected (empty body)',
        example: null
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request POST \\
  --url 'https://staging.agent.aku.africa/business/crypto/customers/quote/REQ123/reject'`
      },
      {
        language: 'javascript',
        code: `const requestId = 'REQ123';
await fetch(\`https://staging.agent.aku.africa/business/crypto/customers/quote/\${requestId}/reject\`, {
  method: 'POST'
});`
      }
    ]
  },
  {
    id: 'check-payment-status',
    title: 'Check Payment Status',
    method: 'GET',
    url: '{baseUrl}/business/crypto/customers/payment-status',
    description: 'Checks the status of a payment request. Poll this endpoint after the customer transfers funds to the virtual account.',
    parameters: [
      {
        name: 'requestId',
        type: 'string',
        required: true,
        location: 'query',
        description: 'The payment request ID',
        example: 'REQ123'
      }
    ],
    responses: [
      {
        status: 200,
        description: 'Payment status',
        example: {
          requestId: 'REQ123',
          status: 'COMPLETED',
          amount: 150000.00,
          paidAt: '2024-01-15T10:45:00Z'
        }
      }
    ],
    codeExamples: [
      {
        language: 'curl',
        code: `curl --request GET \\
  --url 'https://staging.agent.aku.africa/business/crypto/customers/payment-status?requestId=REQ123'`
      },
      {
        language: 'javascript',
        code: `const poll = async (requestId) => {
  const response = await fetch(\`https://staging.agent.aku.africa/business/crypto/customers/payment-status?requestId=\${requestId}\`);
  const data = await response.json();

  if (data.status === 'COMPLETED') {
    console.log('Payment confirmed at', data.paidAt);
    return data;
  }

  await new Promise(r => setTimeout(r, 5000));
  return poll(requestId);
};

poll('REQ123');`
      }
    ]
  }
];